import { lazy, useReducer, useCallback, useEffect, LazyExoticComponent } from 'react';
import { ArrayParam, useQueryParams, withDefault } from 'use-query-params';

export type DrawerTypes =
  | 'Advertiser'
  | 'AppList'
  | 'Audience'
  | 'CustomAudience'
  | 'Order'
  | 'IpList'
  | 'AppAsset'
  | 'User'
  | 'Fund'
  | 'Tracker'
  | 'Exchange'
  | 'RateLimit'
  | 'BidRule';

export type DrawerChild = {
  component: LazyExoticComponent<(props: DrawerChildComponentProps) => JSX.Element>;
  buttons?: TitleButtonType[];
  title?: string | JSX.Element;
};

export const drawerMap: Record<DrawerTypes, DrawerChild> = {
  Advertiser: {
    component: lazy(
      () => import(/* webpackChunkName: "Advertiser" */ '../views/apps/Advertiser/AddAdvertiser'),
    ),
  },
  AppList: {
    component: lazy(
      () => import(/* webpackChunkName: "AppList" */ '../views/apps/AppList/AddAppList'),
    ),
  },
  AppAsset: {
    component: lazy(
      () => import(/* webpackChunkName: "AppList" */ '../views/apps/AppList/AddAppList'),
    ),
  },
  Audience: {
    component: lazy(
      () => import(/* webpackChunkName: "Audience" */ '../views/apps/Audience/AddAudience'),
    ),
  },
  CustomAudience: {
    component: lazy(
      () =>
        import(/* webpackChunkName: "CustomAudience" */ '../views/apps/Audience/AddCustomAudience'),
    ),
  },
  Order: {
    component: lazy(() => import(/* webpackChunkName: "Order" */ '../views/apps/Order/AddOrder')),
  },
  IpList: {
    component: lazy(
      () => import(/* webpackChunkName: "IpList" */ '../views/apps/IpList/AddIpList'),
    ),
  },
  User: {
    component: lazy(() => import(/* webpackChunkName: "User" */ '../views/apps/User/AddUser')),
  },
  Fund: {
    component: lazy(() => import(/* webpackChunkName: "Fund" */ '../views/apps/Fund/AddFund')),
  },
  Tracker: {
    component: lazy(
      () => import(/* webpackChunkName: "Tracker" */ '../views/apps/Creative/Tracker/AddTracker'),
    ),
  },
  Exchange: {
    component: lazy(
      () => import(/* webpackChunkName: "Exchange" */ '../views/apps/Exchange/AddExchange'),
    ),
  },
  RateLimit: {
    component: lazy(
      () => import(/* webpackChunkName: "RateLimit" */ '../views/apps/RateLimit/AddRateLimit'),
    ),
  },
  BidRule: {
    component: lazy(
      () => import(/* webpackChunkName: "BidRule" */ '../views/apps/BidRule/AddBidRule'),
    ),
  },
};

const temporarilyDisabledDrawerTypes: DrawerTypes[] = ['BidRule'];

export const queryParams = {
  drawers: withDefault(ArrayParam, [], true),
  drawerIds: withDefault(ArrayParam, [], true),
};

export interface DrawerData {
  type: DrawerTypes;
  id?: string;
  visible: boolean;
  submitted?: boolean;
  data?: any;
  title?: string | JSX.Element;
  result?: any;
}

type DrawerAction =
  | {
      type: 'OPEN_DRAWER';
      data: Omit<DrawerData, 'visible' | 'submitted'>;
    }
  | {
      type: 'HIDE_DRAWER';
      data: Pick<DrawerData, 'submitted' | 'result'>;
    }
  | {
      type: 'CLOSE_DRAWER' | 'CLOSE_ALL_DRAWER';
    };

function drawerReducer(oldDrawers: DrawerData[], action: DrawerAction) {
  switch (action.type) {
    case 'OPEN_DRAWER':
      return [
        ...oldDrawers,
        { type: action.data.type, id: action.data.id, data: action.data.data, visible: true },
      ];
    case 'CLOSE_DRAWER':
      return oldDrawers.slice(0, -1);
    case 'CLOSE_ALL_DRAWER':
      return [];
    case 'HIDE_DRAWER': {
      if (!oldDrawers.length) return oldDrawers;
      const newDrawers = [...oldDrawers];
      Object.assign(newDrawers[newDrawers.length - 1], {
        visible: false,
        submitted: action.data.submitted,
        result: action.data.result,
      });
      return newDrawers;
    }
    default:
      throw new Error('Invalid drawer action');
  }
}

export type UseDrawerQuery = UseQP<typeof queryParams>;

function initDrawersFromQuery({ drawers, drawerIds }: UseDrawerQuery['query']): DrawerData[] {
  const drawerData: DrawerData[] = [];
  drawers.forEach((drawer, index) => {
    if (
      !drawer ||
      temporarilyDisabledDrawerTypes.includes(drawer as DrawerTypes) ||
      !(drawer in drawerMap)
    )
      return;
    drawerData.push({
      type: drawer as DrawerTypes,
      id: drawerIds[index] || '',
      visible: true,
    });
  });
  return drawerData;
}

export type OpenDrawerFn = (type: DrawerTypes, id?: string, data?: any) => void;
export type CloseDrawerFn = (submitted?: boolean, result?: any) => void;

export function useRightDrawer() {
  const [query, setQuery] = useQueryParams(queryParams);
  const [drawers, dispatch] = useReducer(drawerReducer, query, initDrawersFromQuery);
  const topVisible = drawers[drawers.length - 1]?.visible;

  const openDrawer = useCallback<OpenDrawerFn>(
    (type, id, data) => {
      if (temporarilyDisabledDrawerTypes.includes(type)) return;

      setQuery(
        (prevQuery) => ({
          drawers: [...(prevQuery.drawers ?? []), type],
          drawerIds: [...(prevQuery.drawerIds ?? []), id ?? ''],
        }),
        'pushIn',
      );
      dispatch({ type: 'OPEN_DRAWER', data: { id, type, data } });
    },
    [setQuery],
  );

  const closeAllDrawer = useCallback(() => {
    setQuery({ drawers: [], drawerIds: [] }, 'pushIn');
    dispatch({ type: 'CLOSE_ALL_DRAWER' });
  }, [setQuery]);

  const closeDrawer = useCallback<CloseDrawerFn>((result: any, submitted = false) => {
    // First hide and then remove in effect
    dispatch({ type: 'HIDE_DRAWER', data: { submitted, result } });
  }, []);

  useEffect(() => {
    if (topVisible === false) {
      setTimeout(() => {
        setQuery(
          (prevQuery) => ({
            drawers: prevQuery.drawers.slice(0, -1),
            drawerIds: prevQuery.drawerIds.slice(0, -1),
          }),
          'pushIn',
        );
        dispatch({ type: 'CLOSE_DRAWER' });
      }, 300);
    }
  }, [topVisible, setQuery]);

  return {
    drawers,
    openDrawer,
    closeDrawer,
    closeAllDrawer,
  };
}
